/**
 * 对局 HUD（DOM 部分）
 *
 * 与 Canvas 的分工：**文字信息走 DOM，地图与迷雾走 Canvas**。
 * 中文在 Canvas 里小字号会发虚，DOM 还能吃到系统字体与无障碍能力。
 *
 * 信息层级（渐进披露）：
 * - 常驻（扫一眼就够）：能量 / 智慧 / 迷雾 / 目标进度 / 回合
 * - 一屏之内：可通行路线列表（含花费与不可用原因）
 * - 按需展开：完整技能树、知识图鉴、评分拆解（弹窗）
 *
 * 无障碍：所有状态都是「颜色 + 图标 + 文字」，从不用颜色单独表意。
 */

import type { BlockReason, OptionView, SimEvent, SimState } from "@chuandeng/shared";
import { IGNITE_COST } from "@chuandeng/shared";
import { esc } from "../ui/dom.js";
import { isResourceReason, reasonText } from "../ui/reasons.js";

export interface HudCallbacks {
  onSpread(routeId: string): void;
  onIgnite(regionId: string): void;
  onOpenTree(): void;
  onEndTurn(): void;
}

export interface SelectionView {
  regionId: string | null;
  regionName: string;
  ignitable: boolean;
  reason?: BlockReason;
  currentEnlightenment: number;
}

export interface HudView {
  levelIndex: number;
  levelTitle: string;
  state: SimState;
  options: OptionView;
  log: readonly SimEvent[];
  goal: { current: number; target: number };
  selection: SelectionView;
  /** 有待答的题时禁用"结束回合"，避免跳过考验 */
  quizPending: boolean;
}

export interface Hud {
  hud: HTMLElement;
  actions: HTMLElement;
  side: HTMLElement;
  update(view: HudView): void;
}

function meter(
  modifier: string,
  icon: string,
  label: string,
  value: number,
  max: number,
  valueText: string,
): string {
  const percent = max <= 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return `
    <div class="meter meter--${modifier}">
      <div class="meter-label">
        <span>${icon} ${esc(label)}</span>
        <strong>${esc(valueText)}</strong>
      </div>
      <div class="meter-track" role="progressbar" aria-label="${esc(label)}" aria-valuemin="0" aria-valuemax="${Math.round(max)}" aria-valuenow="${Math.round(value)}">
        <div class="meter-fill" style="width:${percent.toFixed(1)}%"></div>
      </div>
    </div>`;
}

export function createHud(callbacks: HudCallbacks): Hud {
  const hud = document.createElement("section");
  hud.className = "hud paper";

  const actions = document.createElement("section");
  actions.className = "action-bar paper";

  const side = document.createElement("aside");
  side.className = "side";

  return {
    hud,
    actions,
    side,
    update(view) {
      renderHud(hud, view);
      renderActions(actions, view, callbacks);
      renderSide(side, view);
    },
  };
}

function renderHud(host: HTMLElement, view: HudView): void {
  const { state, options, goal } = view;
  const energyCap = Math.max(options.energyCap, state.energy);
  host.innerHTML = `
    <div class="hud-head">
      <h2 class="hud-title">第 ${view.levelIndex} 关 · ${esc(view.levelTitle)}</h2>
      <span class="hud-turn">回合 <strong>${state.turn}</strong> / ${options.turnLimit}${
        view.quizPending ? ' · <span class="badge badge--info">有题目待答</span>' : ""
      }</span>
    </div>
    <div class="meters">
      ${meter("energy", "⚡", "火种能量", state.energy, energyCap, `${Math.round(state.energy)} / ${Math.round(energyCap)}`)}
      ${meter("wisdom", "✧", "智慧值", state.wisdom, Math.max(30, state.wisdom), `${Math.round(state.wisdom)}`)}
      ${meter("mist", "☁", "迷雾浓度", state.mist, 100, `${Math.round(state.mist)}`)}
      ${meter("goal", "◎", "全图启蒙度", goal.current, goal.target, `${goal.current.toFixed(0)} / ${goal.target}`)}
    </div>`;
}

function renderActions(host: HTMLElement, view: HudView, callbacks: HudCallbacks): void {
  const { options, selection, state, quizPending } = view;

  const routeButtons = options.routes
    .filter((option) => isRouteShown(option, state))
    .map((option) => {
      const from = state.regions[option.route.from];
      const fromName = nameOf(view, option.route.from);
      const toName = nameOf(view, option.route.to);
      const hint = option.usable
        ? `${option.route.kind === "sea" ? "海路" : "陆路"} · 目标启蒙度 ${Math.round(
            option.targetEnlightenment,
          )}${from?.ignited ? "" : " · 起点未点燃"}`
        : reasonText(option.reason);
      return `
        <button class="route-btn" type="button" data-route="${esc(option.route.id)}"
                data-focus-key="route:${esc(option.route.id)}"
                ${option.usable && !quizPending ? "" : "disabled"}>
          <span>${esc(fromName)} → ${esc(toName)} <span class="route-cost">${option.cost}⚡</span></span>
          <small>${esc(hint)}</small>
        </button>`;
    })
    .join("");

  const selectionBlock = selection.regionId
    ? `<button class="btn" type="button" data-ignite="${esc(selection.regionId)}"
         data-focus-key="ignite:${esc(selection.regionId)}"
         ${selection.ignitable && !quizPending ? "" : "disabled"}>
         🏕 在「${esc(selection.regionName)}」扎营 <span>${IGNITE_COST}⚡</span>
       </button>
       <small style="color:var(--ink-soft);font-size:.74rem">${
         selection.ignitable
           ? `当前启蒙度 ${Math.round(selection.currentEnlightenment)}`
           : esc(reasonText(selection.reason))
       }</small>`
    : `<small style="color:var(--ink-soft);font-size:.74rem">在地图上点一个区域，可以派火种去扎营</small>`;

  // 重渲染会把按钮整个换掉。键盘/开关设备用户按回车后焦点会掉到 body，
  // 下一次 Tab 得从页面开头重新数 —— 所以记住原来聚焦的是哪个控件，重渲染后还回去。
  const focusKey = focusedControlKey(host);

  host.innerHTML = `
    <div class="route-list">${
      routeButtons || `<small style="color:var(--ink-soft)">这里暂时没有可走的路线</small>`
    }</div>
    <span class="spacer"></span>
    ${selectionBlock}
    <button class="btn btn--ghost" type="button" data-open-tree="1" data-focus-key="tree">✦ 火种技能树</button>
    <button class="btn" type="button" data-end-turn="1" data-focus-key="endTurn" ${quizPending ? "disabled" : ""}>
      结束回合 →
    </button>`;

  // 只在等价控件仍然可用时还原焦点，绝不把焦点塞进 disabled 按钮
  if (focusKey) {
    const next = host.querySelector<HTMLElement>(`[data-focus-key="${focusKey}"]`);
    if (next && !next.hasAttribute("disabled")) next.focus({ preventScroll: true });
  }

  host.onclick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const routeButton = target.closest<HTMLElement>("[data-route]");
    if (routeButton && !routeButton.hasAttribute("disabled")) {
      const routeId = routeButton.dataset["route"];
      if (routeId) callbacks.onSpread(routeId);
      return;
    }
    const igniteButton = target.closest<HTMLElement>("[data-ignite]");
    if (igniteButton && !igniteButton.hasAttribute("disabled")) {
      const regionId = igniteButton.dataset["ignite"];
      if (regionId) callbacks.onIgnite(regionId);
      return;
    }
    if (target.closest("[data-open-tree]")) {
      callbacks.onOpenTree();
      return;
    }
    if (target.closest("[data-end-turn]")) callbacks.onEndTurn();
  };
}

/**
 * 取当前焦点所在控件的稳定标识（仅当焦点确实在本容器内）。
 * 用 data-focus-key 而不是 DOM 引用：重渲染后旧节点就废了。
 */
function focusedControlKey(host: HTMLElement): string | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !host.contains(active)) return null;
  return active.dataset["focusKey"] ?? null;
}

function renderSide(host: HTMLElement, view: HudView): void {
  const { options, state } = view;
  const blocked = options.routes.filter((r) => !r.usable);
  const resourceBlocked = blocked.filter((r) => isResourceReason(r.reason)).length;

  const logItems = view.log
    .slice(-3)
    .reverse()
    .map((event) => {
      const cls = eventClass(event);
      return `<li class="${cls}">第${event.turn}回合 · ${esc(event.message)}</li>`;
    })
    .join("");

  host.innerHTML = `
    <div class="panel paper">
      <h3 class="panel-title">目标 <span>${options.litCount} / ${options.regionCount} 已点亮</span></h3>
      <div class="meter meter--goal">
        <div class="meter-label">
          <span>全图平均启蒙度</span>
          <strong>${options.averageEnlightenment.toFixed(1)} / ${view.goal.target}</strong>
        </div>
        <div class="meter-track"><div class="meter-fill" style="width:${(options.goalProgress * 100).toFixed(1)}%"></div></div>
      </div>
      <p style="margin:.6rem 0 0;font-size:.78rem;color:var(--ink-soft)">
        迷雾每满 25 点，未点亮区域每回合会被侵蚀 1 点；已点亮区域不受影响。
      </p>
      ${
        resourceBlocked > 0
          ? `<p style="margin:.4rem 0 0;font-size:.78rem;color:var(--torch-orange)">有 ${resourceBlocked} 条路线因为能量不足暂时走不了，结束回合可以恢复能量。</p>`
          : ""
      }
      <p style="margin:.4rem 0 0;font-size:.78rem;color:var(--ink-soft)">当前迷雾抗性：${options.effects.mistResist} / 每回合</p>
    </div>
    <div class="panel paper">
      <h3 class="panel-title">路上发生的事</h3>
      ${logItems ? `<ul class="log">${logItems}</ul>` : `<p class="log-empty">还没有特别的事情发生。</p>`}
    </div>`;
}

function nameOf(view: HudView, regionId: string): string {
  // options.regions 里带着完整区域对象，避免再查一次地图数据
  return view.options.regions.find((r) => r.region.id === regionId)?.region.name ?? regionId;
}

/** 隐藏路线不显示（玩家根本不知道它存在，不该出现在列表里） */
function isRouteShown(
  option: OptionView["routes"][number],
  state: SimState,
): boolean {
  if (!option.route.hiddenByDefault) return true;
  return state.revealedRouteIds.includes(option.route.id) || option.reason === "route_need_card";
}

function eventClass(event: SimEvent): string {
  if (event.kind === "mistSpread") return "is-hazard";
  if (event.kind === "randomEvent") {
    return /迷雾|谣言|断绝|疫病|天灾/.test(event.message) ? "is-hazard" : "is-boon";
  }
  return "";
}
